import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person,
  Save,
  Security,
  Notifications,
  Schedule,
  Email,
  Sms,
  Assessment,
  PhotoCamera,
  Visibility,
  Delete,
  History,
  CheckCircle,
  Warning,
  Hive,
  Sensors
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@hooks/redux';
import { selectCurrentUser, updateProfile, updateAvatar, changePassword, updatePreferences } from '@store/slices/authSlice';

import { ApiService } from '@api/index';

interface ProfileFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  timezone: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
}

interface UserActivity {
  id: string | number;
  action: string;
  timestamp: string;
  details: string;
  type: 'login' | 'hive' | 'inspection' | 'alert' | 'device';
}

interface UserStats {
  hivesCount: number;
  devicesCount: number;
  inspectionsCount: number;
  alertsCount: number;
  measurementsCount: number;
  lastLogin: string;
  accountAge: number;
}

const EnhancedProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    timezone: 'Africa/Nairobi',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  // Avatar upload handled by ImageUploader component
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
        phone_number: currentUser.phone_number || '',
        timezone: currentUser.timezone || 'Africa/Nairobi',
        emailNotifications: currentUser.email_notifications ?? true,
        pushNotifications: currentUser.push_notifications ?? true,
        smsNotifications: currentUser.sms_notifications ?? false,
      }));
      loadUserStats();
      loadUserActivity();
    }
  }, [currentUser]);

  const loadUserStats = async () => {
    try {
      const response = await ApiService.get('auth/profile/stats/');
      setUserStats(response.data?.data ?? response.data);
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  };

  const loadUserActivity = async () => {
    try {
      const response = await ApiService.get('auth/profile/activity/');
      const payload = response.data?.data ?? response.data;
      const data = Array.isArray(payload) ? payload : (payload?.results || []);
      setRecentActivity(data);
    } catch (error) {
      console.error('Failed to load user activity:', error);
    }
  };

  const handleAvatarUpload = async (imageFile: any) => {
    try {
      const formData = new FormData();
      formData.append('avatar', imageFile.file);

      await dispatch(updateAvatar(formData)).unwrap();
      setMessage({ type: 'success', text: 'Avatar updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update avatar'
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (showPasswordFields) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error('New passwords do not match');
        }
        if (formData.newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters long');
        }

        await dispatch(changePassword({
          old_password: formData.currentPassword,
          new_password: formData.newPassword,
          new_password_confirm: formData.confirmPassword,
        })).unwrap();

        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        setShowPasswordFields(false);
      } else {
        await dispatch(updateProfile({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          timezone: formData.timezone,
        })).unwrap();

        await dispatch(updatePreferences({
          timezone: formData.timezone,
          email_notifications: formData.emailNotifications,
          push_notifications: formData.pushNotifications,
          sms_notifications: formData.smsNotifications,
        })).unwrap();
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'login': return <Person />;
      case 'hive': return <Hive />;
      case 'inspection': return <Visibility />;
      case 'alert': return <Warning />;
      case 'device': return <Sensors />;
      default: return <History />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'login': return 'info.main';
      case 'hive': return 'primary.main';
      case 'inspection': return 'success.main';
      case 'alert': return 'warning.main';
      case 'device': return 'secondary.main';
      default: return 'text.secondary';
    }
  };

  const displayName = currentUser
    ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username
    : '';

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
          <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', md: 'row' }} textAlign={{ xs: 'center', md: 'left' }}>
            <Box sx={{ position: 'relative', mr: { xs: 0, md: 3 }, mb: { xs: 1, md: 0 } }}>
              <Avatar
                src={currentUser.avatar || undefined}
                sx={{
                  width: { xs: 70, md: 100 },
                  height: { xs: 70, md: 100 },
                  bgcolor: 'primary.main',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                {!currentUser.avatar && (displayName.charAt(0) || <Person fontSize="large" />)}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload-file"
                type="file"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleAvatarUpload({ file: e.target.files[0] });
                  }
                }}
              />
              <label htmlFor="avatar-upload-file">
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: { xs: -5, md: -10 },
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <PhotoCamera sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
                </IconButton>
              </label>
            </Box>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                {displayName || 'User Profile'}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.85rem', md: '1rem' } }}>
                {currentUser.email}
              </Typography>
              <Box display="flex" gap={1} mt={1} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                {currentUser.is_active && (
                  <Chip
                    icon={<CheckCircle />}
                    label="Active"
                    color="success"
                    size="small"
                    sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                  />
                )}
                <Chip
                  icon={<Schedule />}
                  label={`${userStats?.accountAge || 0} days`}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                />
              </Box>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={1.5} sx={{ maxWidth: { xs: '100%', md: 400 } }}>
            <Grid item xs={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1, md: 1.5 }, px: { xs: 0.5, md: 2 } }}>
                  <Typography variant="h6" color="primary" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    {userStats?.hivesCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                    Hives
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1, md: 1.5 }, px: { xs: 0.5, md: 2 } }}>
                  <Typography variant="h6" color="success.main" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    {userStats?.devicesCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                    Devices
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={3}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: { xs: 48, md: 64 },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              padding: { xs: '6px 8px', md: '12px 16px' }
            },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }
          }}
        >
          <Tab label="Profile" icon={<Person />} iconPosition="start" />
          <Tab label="Security" icon={<Security />} iconPosition="start" />
          <Tab label="Activity" icon={<History />} iconPosition="start" />
          <Tab label="Stats" icon={<Assessment />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 } }}>
          {/* Profile Tab */}
          {tabValue === 0 && (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="+254712345678"
                    helperText="Used for SMS alerts. Include country code."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Preferences
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    placeholder="e.g. Africa/Nairobi"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={loading}
                    sx={{
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Save Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {/* Security Tab */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Password & Security
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={showPasswordFields}
                    onChange={(e) => setShowPasswordFields(e.target.checked)}
                  />
                }
                label="Change Password"
                sx={{ mb: 3 }}
              />

              {showPasswordFields && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      name="currentPassword"
                      type="password"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      name="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      required
                      helperText="Minimum 8 characters"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Notifications
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive alerts and updates via email"
                  />
                  <Switch
                    checked={formData.emailNotifications}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emailNotifications: e.target.checked
                    }))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Receive real-time notifications"
                  />
                  <Switch
                    checked={formData.pushNotifications}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      pushNotifications: e.target.checked
                    }))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Sms />
                  </ListItemIcon>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary={
                      formData.phone_number
                        ? `Sent to ${formData.phone_number}`
                        : 'Add a phone number in the Profile tab to enable SMS alerts'
                    }
                  />
                  <Switch
                    checked={formData.smsNotifications}
                    disabled={!formData.phone_number}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      smsNotifications: e.target.checked
                    }))}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setShowDeleteDialog(true)}
                >
                  Delete Account
                </Button>

                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Save Security Settings
                </Button>
              </Box>
            </Box>
          )}

          {/* Activity Tab */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>

              <List>
                {recentActivity.map((activity) => (
                  <ListItem key={activity.id} divider>
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          bgcolor: getActivityColor(activity.type),
                          width: 40,
                          height: 40
                        }}
                      >
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.action}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            {activity.details}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <Chip
                      label={activity.type}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: getActivityColor(activity.type),
                        color: getActivityColor(activity.type)
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Statistics Tab */}
          {tabValue === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Account Statistics
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Hive sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h4">{userStats?.hivesCount || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Hives
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Sensors sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h4">{userStats?.devicesCount || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Connected Devices
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Visibility sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                      <Typography variant="h4">{userStats?.inspectionsCount || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inspections
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Assessment sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                      <Typography variant="h4">{userStats?.measurementsCount?.toLocaleString() || '0'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Measurements
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Delete Account Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Delete sx={{ mr: 1, color: 'error.main' }} />
            Delete Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete your account? This will remove all your hives,
            devices, measurements, and other data from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            color="error"
            variant="contained"
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnhancedProfile;
